import styled from "styled-components";

const palette = ["#1a73e8", "#188038", "#d93025", "#8a63d2", "#f29900"];

const normalizeCode = (code) =>
  String(code || "").replace(/\\_/gu, "_").replace(/\\\*/gu, "*");

const splitTopLevelArgs = (source) => {
  const args = [];
  let current = "";
  let depth = 0;
  let quote = "";

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    const previous = source[index - 1];

    if (quote) {
      current += char;
      if (char === quote && previous !== "\\") quote = "";
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      current += char;
      continue;
    }
    if (char === "(" || char === "[" || char === "{") depth += 1;
    if (char === ")" || char === "]" || char === "}") depth -= 1;
    if (char === "," && depth === 0) {
      args.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }

  if (current.trim()) args.push(current.trim());
  return args;
};

const parseKeywordArgs = (args) => {
  const positional = [];
  const keyword = {};

  args.forEach((arg) => {
    const match = String(arg || "").match(/^([a-zA-Z_]\w*)\s*=\s*([\s\S]+)$/u);
    if (match) {
      keyword[match[1]] = match[2].trim();
      return;
    }
    positional.push(arg);
  });

  return { positional, keyword };
};

const parseNumber = (value) => {
  const parsed = Number(String(value || "").trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const parseString = (value) => {
  const source = String(value || "").trim();
  const match = source.match(/^[rRuU]?["']([\s\S]*)["']$/u);
  return match ? match[1].replace(/\\n/gu, "\n") : null;
};

const parsePreviewLabel = (value) => {
  const label = parseString(value);
  return label === null ? null : label.replace(/\$([^$]+)\$/gu, "$1");
};

const parseBool = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (["true", "1"].includes(normalized)) return true;
  if (["false", "0"].includes(normalized)) return false;
  return null;
};

const parseColor = (value, fallback) => {
  const stringValue = parseString(value);
  if (!stringValue) return fallback;
  const colorMap = {
    b: "#1a73e8",
    black: "#202124",
    blue: "#1a73e8",
    g: "#188038",
    green: "#188038",
    k: "#202124",
    m: "#8a63d2",
    none: "none",
    purple: "#8a63d2",
    r: "#d93025",
    red: "#d93025",
    w: "#ffffff",
    white: "#ffffff",
    y: "#f29900",
    yellow: "#f29900",
  };
  return colorMap[stringValue.toLowerCase()] || stringValue;
};

const parseLineStyle = (value) => {
  const style = parseString(value) || "";
  if (style.includes("--")) return "6 5";
  if (style.includes(":")) return "2 4";
  return "";
};

const getLineDash = (value, variables) => {
  const pattern = evaluateExpression(value, variables);
  if (Array.isArray(pattern) && Array.isArray(pattern[1])) {
    const dashes = pattern[1].filter((length) => Number.isFinite(length) && length > 0);
    if (dashes.length) return dashes.join(" ");
  }
  return parseLineStyle(value);
};

const getLogicalLines = (code) => {
  const lines = [];
  let current = "";
  let depth = 0;
  let quote = "";

  normalizeCode(code)
    .replace(/\r\n?/gu, "\n")
    .split("\n")
    .forEach((rawLine) => {
      const trimmed = rawLine.trim();
      if (!trimmed || trimmed.startsWith("#")) return;

      current = current ? `${current} ${trimmed}` : trimmed;

      for (let index = 0; index < trimmed.length; index += 1) {
        const char = trimmed[index];
        const previous = trimmed[index - 1];
        if (quote) {
          if (char === quote && previous !== "\\") quote = "";
          continue;
        }
        if (char === '"' || char === "'") {
          quote = char;
          continue;
        }
        if (char === "(" || char === "[" || char === "{") depth += 1;
        if (char === ")" || char === "]" || char === "}") depth -= 1;
      }

      if (depth <= 0 && !trimmed.endsWith(",")) {
        lines.push(current.replace(/\s+/gu, " ").trim());
        current = "";
        depth = 0;
        quote = "";
      }
    });

  if (current.trim()) lines.push(current.replace(/\s+/gu, " ").trim());
  return lines;
};

const stripFunctionBlocks = (code) => {
  const keptLines = [];
  let functionIndent = null;

  normalizeCode(code)
    .replace(/\r\n?/gu, "\n")
    .split("\n")
    .forEach((rawLine) => {
      const trimmed = rawLine.trim();
      const indent = rawLine.match(/^\s*/u)?.[0].length ?? 0;

      if (functionIndent !== null) {
        if (!trimmed) return;
        if (indent > functionIndent) return;
        functionIndent = null;
      }

      if (/^\s*def\s+[a-zA-Z_]\w*\s*\(/u.test(rawLine)) {
        functionIndent = indent;
        return;
      }

      keptLines.push(rawLine);
    });

  return keptLines.join("\n");
};

const isIdentifierChar = (char = "") => /[A-Za-z0-9_]/u.test(char);

const substituteIdentifier = (source, name, value) => {
  let result = "";
  let quote = "";

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    const previous = source[index - 1];

    if (quote) {
      result += char;
      if (char === quote && previous !== "\\") quote = "";
      continue;
    }

    if (char === '"' || char === "'") {
      quote = char;
      result += char;
      continue;
    }

    if (
      source.startsWith(name, index) &&
      !isIdentifierChar(source[index - 1]) &&
      !isIdentifierChar(source[index + name.length])
    ) {
      result += value;
      index += name.length - 1;
      continue;
    }

    result += char;
  }

  return result;
};

const expandSimpleForBlocks = (code) => {
  const lines = normalizeCode(code).replace(/\r\n?/gu, "\n").split("\n");
  const expanded = [];

  for (let index = 0; index < lines.length; index += 1) {
    const rawLine = lines[index];
    const match = rawLine.match(/^(\s*)for\s+([a-zA-Z_]\w*)\s+in\s+(\[[\s\S]*\]|\([\s\S]*\))\s*:\s*$/u);
    if (!match) {
      expanded.push(rawLine);
      continue;
    }

    const indent = match[1].length;
    const valuesSource = stripOuterParens(match[3].trim());
    const isSequence =
      (valuesSource.startsWith("[") && valuesSource.endsWith("]")) ||
      (valuesSource.startsWith("(") && valuesSource.endsWith(")"));
    if (!isSequence) {
      expanded.push(rawLine);
      continue;
    }

    const values = splitTopLevelArgs(valuesSource.slice(1, -1));
    const bodyLines = [];
    let cursor = index + 1;
    while (cursor < lines.length) {
      const nextLine = lines[cursor];
      const trimmed = nextLine.trim();
      const nextIndent = nextLine.match(/^\s*/u)?.[0].length ?? 0;
      if (trimmed && nextIndent <= indent) break;
      if (trimmed) bodyLines.push(trimmed);
      cursor += 1;
    }

    if (!values.length || !bodyLines.length) {
      expanded.push(rawLine);
      continue;
    }

    values.forEach((value) => {
      bodyLines.forEach((bodyLine) => {
        expanded.push(substituteIdentifier(bodyLine, match[2], value.trim()));
      });
    });
    index = cursor - 1;
  }

  return expanded.join("\n");
};

const getCallArgs = (line, name) => {
  const pattern = new RegExp(`(?:plt\\.|ax\\.)${name}\\s*\\(([\\s\\S]*)\\)`, "u");
  const match = line.match(pattern);
  return match ? splitTopLevelArgs(match[1]) : null;
};

const getNamedCallArgs = (line, name) => {
  const pattern = new RegExp(`(?:^|[^\\w.])${name}\\s*\\(([\\s\\S]*)\\)\\s*$`, "u");
  const match = line.match(pattern);
  return match ? splitTopLevelArgs(match[1]) : null;
};

const getAnyCallArgs = (line, names) => {
  for (const name of names) {
    const args = getCallArgs(line, name);
    if (args) return { name, args };
  }
  return null;
};

const isNumberArray = (value) =>
  Array.isArray(value) && value.length > 0 && value.every(Number.isFinite);

const getPoint = (value) => {
  if (Array.isArray(value) && value.length >= 2) {
    const [x, y] = value;
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
  }
  return value && Number.isFinite(value.x) && Number.isFinite(value.y)
    ? { x: value.x, y: value.y }
    : null;
};

const hasTopLevelComma = (source) => splitTopLevelArgs(source).length > 1;

const isWrappedExpression = (source) => {
  if (!source.startsWith("(") || !source.endsWith(")")) return false;
  let depth = 0;
  let quote = "";

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    const previous = source[index - 1];
    if (quote) {
      if (char === quote && previous !== "\\") quote = "";
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      continue;
    }
    if (char === "(") depth += 1;
    if (char === ")") depth -= 1;
    if (depth === 0 && index < source.length - 1) return false;
  }

  return depth === 0 && !hasTopLevelComma(source.slice(1, -1));
};

const stripOuterParens = (value) => {
  let source = String(value || "").trim();
  while (isWrappedExpression(source)) source = source.slice(1, -1).trim();
  return source;
};

const getPreviousSignificantChar = (source, index) => {
  for (let offset = index - 1; offset >= 0; offset -= 1) {
    if (!/\s/u.test(source[offset])) return source[offset];
  }
  return "";
};

const findTopLevelBinaryOperator = (source, operators) => {
  const positions = [];
  let depth = 0;
  let quote = "";

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    const previous = source[index - 1];
    const next = source[index + 1];

    if (quote) {
      if (char === quote && previous !== "\\") quote = "";
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      continue;
    }
    if (char === "(" || char === "[" || char === "{") {
      depth += 1;
      continue;
    }
    if (char === ")" || char === "]" || char === "}") {
      depth -= 1;
      continue;
    }
    if (depth !== 0) continue;
    if (operators.includes("**") && char === "*" && next === "*") {
      positions.push({ index, operator: "**" });
      index += 1;
      continue;
    }
    if (!operators.includes(char)) continue;
    const previousSignificant = getPreviousSignificantChar(source, index);
    if (
      (char === "+" || char === "-") &&
      (!previousSignificant || /[+\-*/(,[{]/u.test(previousSignificant))
    ) {
      continue;
    }
    if (char === "*" && (previous === "*" || next === "*")) continue;
    positions.push({ index, operator: char });
  }

  return positions.length ? positions[positions.length - 1] : null;
};

const mapNumber = (value, mapper) => {
  if (Number.isFinite(value)) return mapper(value);
  if (isNumberArray(value)) return value.map(mapper);
  return null;
};

const applyBinaryOperator = (left, right, operator) => {
  const leftArray = isNumberArray(left);
  const rightArray = isNumberArray(right);

  if (operator === "+") {
    if (Number.isFinite(left) && Number.isFinite(right)) return left + right;
    if (leftArray && rightArray && left.length === right.length) {
      return left.map((item, index) => item + right[index]);
    }
    if (leftArray && Number.isFinite(right)) return left.map((item) => item + right);
    if (Number.isFinite(left) && rightArray) return right.map((item) => left + item);
  }
  if (operator === "-") {
    if (Number.isFinite(left) && Number.isFinite(right)) return left - right;
    if (leftArray && rightArray && left.length === right.length) {
      return left.map((item, index) => item - right[index]);
    }
    if (leftArray && Number.isFinite(right)) return left.map((item) => item - right);
    if (Number.isFinite(left) && rightArray) return right.map((item) => left - item);
  }
  if (operator === "*") {
    if (Number.isFinite(left) && Number.isFinite(right)) return left * right;
    if (leftArray && Number.isFinite(right)) return left.map((item) => item * right);
    if (Number.isFinite(left) && rightArray) return right.map((item) => left * item);
  }
  if (operator === "/") {
    if (Number.isFinite(right) && right === 0) return null;
    if (Number.isFinite(left) && Number.isFinite(right)) return left / right;
    if (leftArray && Number.isFinite(right)) return left.map((item) => item / right);
  }
  if (operator === "**" && Number.isFinite(left) && Number.isFinite(right)) {
    return Math.pow(left, right);
  }
  return null;
};

const evaluateSequenceLiteral = (source, variables) => {
  const trimmed = source.trim();
  const npArray = trimmed.match(/^np\.array\s*\(([\s\S]+)\)$/u);
  if (npArray) {
    const { positional } = parseKeywordArgs(splitTopLevelArgs(npArray[1]));
    return positional.length ? evaluateExpression(positional[0], variables) : null;
  }

  const isList = trimmed.startsWith("[") && trimmed.endsWith("]");
  const isTuple = trimmed.startsWith("(") && trimmed.endsWith(")");
  if (!isList && !isTuple) return null;
  const inner = trimmed.slice(1, -1).trim();
  if (!inner) return [];
  if (isTuple && !hasTopLevelComma(inner)) return null;

  const values = splitTopLevelArgs(inner).map((item) =>
    evaluateExpression(item, variables),
  );
  return values.every((item) => item !== null) ? values : null;
};

const evaluateExpression = (expr, variables) => {
  const original = String(expr || "").trim();
  if (!original) return null;

  const sequence = evaluateSequenceLiteral(original, variables);
  if (sequence !== null) return sequence;

  let source = stripOuterParens(original);
  const number = parseNumber(source);
  if (number !== null) return number;
  if (source === "np.pi" || source === "math.pi" || source === "pi") return Math.PI;
  if (source.startsWith("*")) source = source.slice(1).trim();
  if (source.startsWith("+")) return evaluateExpression(source.slice(1), variables);
  if (source.startsWith("-")) {
    const value = evaluateExpression(source.slice(1), variables);
    return mapNumber(value, (item) => -item);
  }
  if (variables.has(source)) return variables.get(source);

  const indexed = source.match(/^([a-zA-Z_]\w*)\s*\[\s*(\d+)\s*\]$/u);
  if (indexed) {
    const value = variables.get(indexed[1]);
    const index = Number(indexed[2]);
    return Array.isArray(value) && Number.isFinite(value[index]) ? value[index] : null;
  }

  const column = source.match(/^([a-zA-Z_]\w*)\s*\[\s*:\s*,\s*(\d+)\s*\]$/u);
  if (column) {
    const rows = variables.get(column[1]);
    const index = Number(column[2]);
    if (Array.isArray(rows) && rows.every((row) => Array.isArray(row) && Number.isFinite(row[index]))) {
      return rows.map((row) => row[index]);
    }
  }

  const maxCall = source.match(/^max\s*\(([\s\S]+)\)$/u);
  if (maxCall) {
    const args = splitTopLevelArgs(maxCall[1]);
    const values = evaluateExpression(args[0], variables);
    const keyArg = args.find((item) => /^\s*key\s*=/u.test(item));
    const keyIndex = keyArg?.match(/lambda\s+([a-zA-Z_]\w*)\s*:\s*\1\s*\[\s*(\d+)\s*\]/u);
    if (Array.isArray(values) && keyIndex) {
      const index = Number(keyIndex[2]);
      return values.reduce((best, item) => {
        if (!Array.isArray(item) || !Number.isFinite(item[index])) return best;
        return !best || item[index] > best[index] ? item : best;
      }, null);
    }
  }

  const functionCall = source.match(/^((?:np\.|math\.)?[a-zA-Z_]\w*)\s*\(([\s\S]*)\)$/u);
  if (functionCall) {
    const fnName = functionCall[1].replace(/^(np|math)\./u, "");
    const args = splitTopLevelArgs(functionCall[2]).map((item) =>
      evaluateExpression(item, variables),
    );
    const unary = {
      abs: Math.abs,
      arccos: Math.acos,
      arcsin: Math.asin,
      arctan: Math.atan,
      cos: Math.cos,
      exp: Math.exp,
      log: Math.log,
      sin: Math.sin,
      sqrt: Math.sqrt,
      tan: Math.tan,
    };
    if (fnName === "dot" && isNumberArray(args[0]) && isNumberArray(args[1])) {
      if (args[0].length !== args[1].length) return null;
      return args[0].reduce((total, item, index) => total + item * args[1][index], 0);
    }
    if (unary[fnName] && args.length === 1) return mapNumber(args[0], unary[fnName]);
  }

  for (const operators of [["+", "-"], ["*", "/"], ["**"]]) {
    const binary = findTopLevelBinaryOperator(source, operators);
    if (!binary) continue;
    const left = evaluateExpression(source.slice(0, binary.index), variables);
    const right = evaluateExpression(
      source.slice(binary.index + binary.operator.length),
      variables,
    );
    const value = applyBinaryOperator(left, right, binary.operator);
    if (value !== null) return value;
  }

  return null;
};

const parseStyleDict = (value) => {
  const match = String(value || "").trim().match(/^dict\s*\(([\s\S]*)\)$/u);
  if (!match) return null;
  return {
    __style: true,
    values: parseKeywordArgs(splitTopLevelArgs(match[1])).keyword,
  };
};

const mergeSpreadKeywordArgs = (positional, keyword, variables) => {
  const merged = { ...keyword };
  const cleanPositional = [];
  positional.forEach((item) => {
    const spread = String(item || "").trim().match(/^\*\*([a-zA-Z_]\w*)$/u);
    if (!spread) {
      cleanPositional.push(item);
      return;
    }
    const style = variables.get(spread[1]);
    if (!style?.__style) return;
    Object.entries(style.values).forEach(([key, value]) => {
      if (!(key in merged)) merged[key] = value;
    });
  });
  return { positional: cleanPositional, keyword: merged };
};

const linspace = (start, stop, count = 80) => {
  const safeCount = Math.max(2, Math.min(240, Math.round(count || 80)));
  return Array.from(
    { length: safeCount },
    (_, index) => start + ((stop - start) * index) / (safeCount - 1),
  );
};

const buildHermiteCurve = (code, variables) => {
  const axisSource = code.match(/for\s+i\s+in\s+range\s*\(\s*len\((points|xs)\)\s*-\s*1\s*\)/u)?.[1];
  if (
    !axisSource ||
    !/curve_x\.extend\s*\(/u.test(code) ||
    !/curve_y\.extend\s*\(/u.test(code) ||
    !/t\s*\*\*\s*3/u.test(code) ||
    !/slopes\[i\s*\+\s*1\]/u.test(code)
  ) return null;

  const points = axisSource === "points"
    ? variables.get("points")
    : variables.get("xs")?.map((x, index) => [x, variables.get("ys")?.[index]]);
  const slopes = variables.get("slopes");
  if (!Array.isArray(points) || points.length < 2 || !isNumberArray(slopes) || slopes.length !== points.length) return null;
  if (!points.every((point) => Array.isArray(point) && point.length === 2 && point.every(Number.isFinite))) return null;

  const sampleCount = Number(code.match(/t\s*=\s*np\.linspace\s*\(\s*0\s*,\s*1\s*,\s*(\d+)\s*\)/u)?.[1]) || 90;
  const x = [];
  const y = [];
  for (let index = 0; index < points.length - 1; index += 1) {
    const [x0, y0] = points[index];
    const [x1, y1] = points[index + 1];
    const h = x1 - x0;
    if (h <= 0) return null;
    linspace(0, 1, sampleCount).forEach((t) => {
      x.push(x0 + h * t);
      y.push(
        (2 * t ** 3 - 3 * t ** 2 + 1) * y0 +
        (t ** 3 - 2 * t ** 2 + t) * h * slopes[index] +
        (-2 * t ** 3 + 3 * t ** 2) * y1 +
        (t ** 3 - t ** 2) * h * slopes[index + 1],
      );
    });
  }
  return { x, y };
};

const expandBounds = (min, max) => {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [-5, 5];
  if (min === max) {
    const pad = Math.max(1, Math.abs(min || 1) * 0.2);
    return [min - pad, max + pad];
  }
  const pad = (max - min) * 0.12;
  return [min - pad, max + pad];
};

const getConstructorArgs = (source, name) => {
  const match = String(source || "").match(new RegExp(`\\b${name}\\s*\\(`, "u"));
  if (!match) return null;
  const start = match.index + match[0].length;
  let depth = 1;
  let quote = "";

  for (let index = start; index < source.length; index += 1) {
    const char = source[index];
    const previous = source[index - 1];
    if (quote) {
      if (char === quote && previous !== "\\") quote = "";
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      continue;
    }
    if (char === "(") depth += 1;
    if (char === ")") depth -= 1;
    if (depth === 0) return splitTopLevelArgs(source.slice(start, index));
  }
  return null;
};

const getConstructorKeywordArgs = (source, name, variables) => {
  const args = getConstructorArgs(source, name);
  if (!args) return null;
  let { positional, keyword } = parseKeywordArgs(args);
  ({ positional, keyword } = mergeSpreadKeywordArgs(positional, keyword, variables));
  return { positional, keyword };
};

const pathCodeNames = new Set([
  "STOP",
  "MOVETO",
  "LINETO",
  "CURVE3",
  "CURVE4",
  "CLOSEPOLY",
]);

const numericPathCodes = new Map([
  [0, "STOP"],
  [1, "MOVETO"],
  [2, "LINETO"],
  [3, "CURVE3"],
  [4, "CURVE4"],
  [79, "CLOSEPOLY"],
]);

const getDefaultPathCodes = (count) =>
  Array.from({ length: Math.max(0, count) }, (_, index) =>
    index === 0 ? "MOVETO" : "LINETO",
  );

const parsePathCode = (value) => {
  const source = String(value || "").trim().replace(/^Path\./u, "");
  if (pathCodeNames.has(source)) return source;
  const numeric = parseNumber(source);
  return numericPathCodes.get(numeric) || null;
};

const normalizePathCodes = (values, vertexCount = 0) => {
  const codes = (values || []).map(parsePathCode);
  if (!codes.length || codes.some((code) => !code)) {
    return vertexCount ? getDefaultPathCodes(vertexCount) : null;
  }
  if (!vertexCount) return codes;
  return getDefaultPathCodes(vertexCount).map((fallback, index) => codes[index] || fallback);
};

const parsePathCodeExpression = (value, variables) => {
  const source = stripOuterParens(String(value || "").trim());
  if (!source) return null;

  const variable = variables.get(source);
  if (Array.isArray(variable)) return variable;

  const plus = findTopLevelBinaryOperator(source, ["+"]);
  if (plus) {
    const left = parsePathCodeExpression(source.slice(0, plus.index), variables);
    const right = parsePathCodeExpression(
      source.slice(plus.index + plus.operator.length),
      variables,
    );
    return left && right ? [...left, ...right] : null;
  }

  const multiplier = findTopLevelBinaryOperator(source, ["*"]);
  if (multiplier) {
    const left = parsePathCodeExpression(source.slice(0, multiplier.index), variables);
    const right = evaluateExpression(
      source.slice(multiplier.index + multiplier.operator.length),
      variables,
    );
    if (left && Number.isFinite(right)) {
      return Array.from({ length: Math.max(0, Math.round(right)) }).flatMap(() => left);
    }

    const rightCodes = parsePathCodeExpression(
      source.slice(multiplier.index + multiplier.operator.length),
      variables,
    );
    const leftCount = evaluateExpression(source.slice(0, multiplier.index), variables);
    if (rightCodes && Number.isFinite(leftCount)) {
      return Array.from({ length: Math.max(0, Math.round(leftCount)) }).flatMap(
        () => rightCodes,
      );
    }
  }

  if (
    (source.startsWith("[") && source.endsWith("]")) ||
    (source.startsWith("(") && source.endsWith(")"))
  ) {
    return normalizePathCodes(splitTopLevelArgs(source.slice(1, -1)));
  }

  const code = parsePathCode(source);
  return code ? [code] : null;
};

const parsePathCodes = (value, variables, vertexCount = 0) => {
  const source = String(value || "").trim();
  if (!source) return vertexCount ? getDefaultPathCodes(vertexCount) : null;

  const variable = variables.get(source);
  if (Array.isArray(variable)) return normalizePathCodes(variable, vertexCount);

  const expressionCodes = parsePathCodeExpression(source, variables);
  if (expressionCodes) return normalizePathCodes(expressionCodes, vertexCount);

  const evaluated = evaluateExpression(source, variables);
  if (Array.isArray(evaluated)) return normalizePathCodes(evaluated, vertexCount);

  const stripped = stripOuterParens(source);
  if (
    (stripped.startsWith("[") && stripped.endsWith("]")) ||
    (stripped.startsWith("(") && stripped.endsWith(")"))
  ) {
    return normalizePathCodes(splitTopLevelArgs(stripped.slice(1, -1)), vertexCount);
  }

  return vertexCount ? getDefaultPathCodes(vertexCount) : null;
};

const normalizePathVertices = (value) => {
  if (!Array.isArray(value)) return null;
  const vertices = value.map(getPoint);
  return vertices.every(Boolean) ? vertices : null;
};

const parsePathDefinition = (value, variables) => {
  const source = String(value || "").trim();
  const variable = variables.get(source);
  if (variable?.__path) return variable;

  const call = getConstructorKeywordArgs(source, "Path", variables);
  if (!call) return null;

  const vertices = normalizePathVertices(
    evaluateExpression(call.positional[0], variables),
  );
  if (!vertices?.length) return null;

  return {
    __path: true,
    codes: parsePathCodes(call.positional[1], variables, vertices.length),
    vertices,
  };
};

const getLineWidth = (keyword, variables, fallback = 2) => {
  const value = evaluateExpression(keyword.linewidth || keyword.lw, variables);
  return Number.isFinite(value) ? Math.max(1, Math.min(8, value)) : fallback;
};

const getAlpha = (keyword, variables) => {
  const value = evaluateExpression(keyword.alpha, variables);
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 1;
};

const getMarkerRadius = (keyword, variables, fallbackArea = 90) => {
  const markerSize = evaluateExpression(keyword.markersize || keyword.ms, variables);
  if (Number.isFinite(markerSize)) return Math.max(2, Math.min(12, markerSize * 0.72));
  const area = evaluateExpression(keyword.s, variables) || fallbackArea;
  return Math.max(3, Math.min(9, Math.sqrt(area) / 3));
};

const parseRangePair = (value, variables) => {
  const evaluated = evaluateExpression(value, variables);
  if (Array.isArray(evaluated) && Number.isFinite(evaluated[0]) && Number.isFinite(evaluated[1])) {
    return [evaluated[0], evaluated[1]];
  }
  return null;
};

const makePathPatch = (path, keyword, variables, fallbackWidth = 2, handDrawn = false) => {
  const fill = parseBool(keyword.fill) ?? false;
  return {
    capStyle: parseString(keyword.capstyle || keyword.solid_capstyle) || "round",
    codes: path.codes,
    fill: fill
      ? parseColor(keyword.facecolor || keyword.fc || keyword.color || keyword.c, "#eef4ff")
      : "none",
    handDrawn,
    joinStyle: parseString(keyword.joinstyle || keyword.solid_joinstyle) || "round",
    opacity: getAlpha(keyword, variables),
    stroke: parseColor(keyword.edgecolor || keyword.ec || keyword.color || keyword.c, "#202124"),
    strokeWidth: getLineWidth(keyword, variables, fallbackWidth),
    type: "path",
    vertices: path.vertices,
  };
};

const parseSubplotSize = (line, variables) => {
  const args = getCallArgs(line, "subplots");
  if (!args) return null;
  const { keyword } = parseKeywordArgs(args);
  const size = parseRangePair(keyword.figsize, variables);
  if (!size) return null;
  const [width, height] = size;
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
  return { width, height };
};

const shouldIgnoreMatplotlibLine = (line) =>
  /^plt\.tight_layout\b/u.test(line) ||
  /^plt\.show\b/u.test(line) ||
  /^plt\.close\b/u.test(line) ||
  /^fig\./u.test(line) ||
  /^ax\.figure\./u.test(line);

const parsePatch = (line, variables) => {
  const arrowCall = getConstructorKeywordArgs(line, "FancyArrowPatch", variables);
  if (arrowCall) {
    const { positional, keyword } = arrowCall;
    const start = getPoint(evaluateExpression(positional[0], variables));
    const end = getPoint(evaluateExpression(positional[1], variables));
    if (start && end) {
      return {
        type: "arrow",
        start,
        end,
        headSize: Math.max(6, Math.min(18, (evaluateExpression(keyword.mutation_scale, variables) || 20) * 0.45)),
        opacity: getAlpha(keyword, variables),
        stroke: parseColor(keyword.color || keyword.edgecolor, "#202124"),
        strokeWidth: getLineWidth(keyword, variables, 2),
      };
    }
  }

  const pathPatchCall = getConstructorKeywordArgs(line, "PathPatch", variables);
  if (pathPatchCall) {
    const { positional, keyword } = pathPatchCall;
    const path = parsePathDefinition(positional[0], variables);
    if (path) {
      return makePathPatch(path, keyword, variables);
    }
  }

  const polygonCall = getConstructorKeywordArgs(line, "Polygon", variables);
  if (polygonCall) {
    const { positional, keyword } = polygonCall;
    const vertices = normalizePathVertices(
      evaluateExpression(positional[0], variables),
    );
    const fill = parseBool(keyword.fill) ?? true;
    if (vertices?.length) {
      return {
        closed: parseBool(keyword.closed) ?? true,
        fill: fill
          ? parseColor(keyword.facecolor || keyword.fc || keyword.color || keyword.c, "#eef4ff")
          : "none",
        joinStyle: parseString(keyword.joinstyle || keyword.solid_joinstyle) || "round",
        opacity: getAlpha(keyword, variables),
        stroke: parseColor(keyword.edgecolor || keyword.ec || keyword.color || keyword.c, "#202124"),
        strokeWidth: getLineWidth(keyword, variables, 2),
        type: "polygon",
        vertices,
      };
    }
  }

  const circleCall = getConstructorKeywordArgs(line, "Circle", variables);
  if (circleCall) {
    const { positional, keyword } = circleCall;
    const center = getPoint(evaluateExpression(positional[0], variables));
    const radius = evaluateExpression(positional[1], variables);
    const fill = parseBool(keyword.fill) ?? false;
    if (center && Number.isFinite(radius)) {
      return {
        center,
        fill: fill ? parseColor(keyword.facecolor || keyword.fc, "#eef4ff") : "none",
        opacity: getAlpha(keyword, variables),
        radius,
        stroke: parseColor(keyword.edgecolor || keyword.ec || keyword.color || keyword.c, "#202124"),
        strokeWidth: getLineWidth(keyword, variables, 2),
        type: "circle",
      };
    }
  }

  const rectangleCall = getConstructorKeywordArgs(line, "Rectangle", variables);
  if (rectangleCall) {
    const { positional, keyword } = rectangleCall;
    const origin = getPoint(evaluateExpression(positional[0], variables));
    const width = evaluateExpression(positional[1], variables);
    const height = evaluateExpression(positional[2], variables);
    const fill = parseBool(keyword.fill) ?? false;
    if (origin && Number.isFinite(width) && Number.isFinite(height)) {
      return {
        fill: fill ? parseColor(keyword.facecolor || keyword.fc, "#f0f8f2") : "none",
        height,
        opacity: getAlpha(keyword, variables),
        origin,
        stroke: parseColor(keyword.edgecolor || keyword.ec || keyword.color || keyword.c, "#202124"),
        strokeWidth: getLineWidth(keyword, variables, 2),
        type: "rectangle",
        width,
      };
    }
  }

  return null;
};

const addPatchBounds = (bounds, patch) => {
  if (patch.type === "arrow") {
    bounds.x.push(patch.start.x, patch.end.x);
    bounds.y.push(patch.start.y, patch.end.y);
  }
  if (patch.type === "circle") {
    bounds.x.push(patch.center.x - patch.radius, patch.center.x + patch.radius);
    bounds.y.push(patch.center.y - patch.radius, patch.center.y + patch.radius);
  }
  if (patch.type === "rectangle") {
    bounds.x.push(patch.origin.x, patch.origin.x + patch.width);
    bounds.y.push(patch.origin.y, patch.origin.y + patch.height);
  }
  if (patch.type === "path" || patch.type === "polygon") {
    patch.vertices.forEach((point) => {
      bounds.x.push(point.x);
      bounds.y.push(point.y);
    });
  }
};

const parseQuestionMatplotlib = (code) => {
  const plots = [];
  const scatters = [];
  const texts = [];
  const patches = [];
  const bounds = { x: [], y: [] };
  const variables = new Map();
  let figureSize = null;
  let xlim = null;
  let ylim = null;
  let equalAspect = false;
  let axisOff = false;
  let title = "";
  let xlabel = "";
  let ylabel = "";
  let background = "#ffffff";
  let unsupportedCount = 0;

  const addBounds = (xValues, yValues) => {
    bounds.x.push(...xValues.filter(Number.isFinite));
    bounds.y.push(...yValues.filter(Number.isFinite));
  };

  getLogicalLines(expandSimpleForBlocks(stripFunctionBlocks(code))).forEach((line) => {
    if (/^(import|from)\b/u.test(line)) return;
    if (/^plt\.rcParams/u.test(line)) return;
    if (/^plt\.subplots_adjust\b/u.test(line)) return;
    if (/^[a-zA-Z_]\w*\.set_sketch_params\b/u.test(line)) return;
    if (/^fig\s*,\s*ax\s*=\s*plt\.subplots/u.test(line)) {
      figureSize = parseSubplotSize(line, variables) || figureSize;
      return;
    }

    const faceColorAssignment = line.match(/^(?:fig\.patch|ax)\.set_facecolor\s*\(([\s\S]+)\)$/u);
    if (faceColorAssignment) {
      background = parseColor(splitTopLevelArgs(faceColorAssignment[1])[0], background);
      return;
    }

    if (shouldIgnoreMatplotlibLine(line)) return;

    const linspaceAssignment = line.match(
      /^([a-zA-Z_]\w*)\s*=\s*np\.linspace\s*\(([\s\S]+)\)$/u,
    );
    if (linspaceAssignment) {
      const args = splitTopLevelArgs(linspaceAssignment[2]).map((item) =>
        evaluateExpression(item, variables),
      );
      if (Number.isFinite(args[0]) && Number.isFinite(args[1])) {
        variables.set(linspaceAssignment[1], linspace(args[0], args[1], args[2] || 80));
      }
      return;
    }

    const assignment = line.match(/^([a-zA-Z_]\w*)\s*=\s*([\s\S]+)$/u);
    if (assignment && !line.includes("plt.") && !line.includes("ax.")) {
      variables.set(
        assignment[1],
        parsePathDefinition(assignment[2], variables) ||
          (assignment[2].includes("Path.")
            ? parsePathCodes(assignment[2], variables)
            : null) ||
          parseStyleDict(assignment[2]) ||
          evaluateExpression(assignment[2], variables),
      );
      if (["slopes", "points", "xs", "ys"].includes(assignment[1])) {
        const generated = buildHermiteCurve(code, variables);
        if (generated) {
          variables.set("curve_x", generated.x);
          variables.set("curve_y", generated.y);
        }
      }
      return;
    }

    const handLineArgs = getNamedCallArgs(line, "hand_line");
    if (handLineArgs) {
      let { positional, keyword } = parseKeywordArgs(handLineArgs);
      ({ positional, keyword } = mergeSpreadKeywordArgs(positional, keyword, variables));
      const xValues = evaluateExpression(positional[0], variables);
      const yValues = evaluateExpression(positional[1], variables);
      if (isNumberArray(xValues) && isNumberArray(yValues) && xValues.length === yValues.length) {
        plots.push({
          color: parseColor(keyword.color || keyword.c, "#202124"),
          dash: getLineDash(keyword.linestyle || keyword.ls, variables),
          handDrawn: true,
          linewidth: getLineWidth(keyword, variables, 2.5),
          opacity: getAlpha(keyword, variables),
          x: xValues,
          y: yValues,
        });
        addBounds(xValues, yValues);
      } else {
        unsupportedCount += 1;
      }
      return;
    }

    const handPathArgs = getNamedCallArgs(line, "hand_path");
    if (handPathArgs) {
      let { positional, keyword } = parseKeywordArgs(handPathArgs);
      ({ positional, keyword } = mergeSpreadKeywordArgs(positional, keyword, variables));
      const vertices = normalizePathVertices(evaluateExpression(positional[0], variables));
      const path = vertices?.length
        ? {
            __path: true,
            codes: parsePathCodes(positional[1], variables, vertices.length),
            vertices,
          }
        : null;
      if (path) {
        const patch = makePathPatch(path, keyword, variables, 2.4, true);
        patches.push(patch);
        addPatchBounds(bounds, patch);
      } else {
        unsupportedCount += 1;
      }
      return;
    }

    const call = getAnyCallArgs(line, [
      "add_artist",
      "add_patch",
      "annotate",
      "axis",
      "axhline",
      "axvline",
      "hlines",
      "plot",
      "scatter",
      "set",
      "set_aspect",
      "set_title",
      "set_xlim",
      "set_ylim",
      "set_xlabel",
      "set_ylabel",
      "text",
      "title",
      "xlim",
      "xlabel",
      "ylim",
      "ylabel",
    ]);

    if (!call) {
      if (/^(ax|plt)\./u.test(line)) unsupportedCount += 1;
      return;
    }

    let { positional, keyword } = parseKeywordArgs(call.args);
    ({ positional, keyword } = mergeSpreadKeywordArgs(positional, keyword, variables));
    const colorIndex = plots.length + scatters.length;
    const color = parseColor(keyword.color || keyword.c, palette[colorIndex % palette.length]);
    const dash = getLineDash(keyword.linestyle || keyword.linestyles || keyword.ls || positional[2], variables);
    const linewidth = getLineWidth(keyword, variables, 3);
    const opacity = getAlpha(keyword, variables);

    if (call.name === "add_patch" || call.name === "add_artist") {
      const patch = parsePatch(positional[0] || line, variables);
      if (patch) {
        patches.push(patch);
        addPatchBounds(bounds, patch);
      } else {
        unsupportedCount += 1;
      }
      return;
    }

    if (call.name === "set") {
      const nextXlim = parseRangePair(keyword.xlim, variables);
      const nextYlim = parseRangePair(keyword.ylim, variables);
      if (nextXlim) xlim = nextXlim;
      if (nextYlim) ylim = nextYlim;
      if (parseString(keyword.aspect) === "equal") equalAspect = true;
      const nextTitle = parseString(keyword.title);
      const nextXlabel = parseString(keyword.xlabel);
      const nextYlabel = parseString(keyword.ylabel);
      if (nextTitle) title = nextTitle;
      if (nextXlabel) xlabel = nextXlabel;
      if (nextYlabel) ylabel = nextYlabel;
      return;
    }

    if (call.name === "plot" || call.name === "scatter") {
      const zipped = call.name === "scatter" && positional[0]?.match(/^\*zip\(\*([a-zA-Z_]\w*)\)$/u);
      if (zipped) {
        const points = variables.get(zipped[1]);
        if (Array.isArray(points) && points.length && points.every((entry) => Array.isArray(entry) && entry.length === 2 && entry.every(Number.isFinite))) {
          const x = points.map((entry) => entry[0]);
          const y = points.map((entry) => entry[1]);
          scatters.push({ color, opacity, radius: getMarkerRadius(keyword, variables), x, y });
          addBounds(x, y);
        } else unsupportedCount += 1;
        return;
      }
      const point = call.name === "scatter" && !positional[1]
        ? getPoint(evaluateExpression(positional[0], variables))
        : null;
      if (point) {
        scatters.push({
          color,
          opacity,
          radius: getMarkerRadius(keyword, variables),
          x: [point.x],
          y: [point.y],
        });
        addBounds([point.x], [point.y]);
        return;
      }

      let xValues = evaluateExpression(positional[0], variables);
      let yValues = evaluateExpression(positional[1], variables);
      if (Array.isArray(xValues) && !positional[1]) {
        yValues = xValues;
        xValues = xValues.map((_, index) => index);
      }
      const marker = parseString(keyword.marker || keyword.markersymbol) || parseString(positional[2]) || "";
      if (
        Number.isFinite(xValues) &&
        Number.isFinite(yValues) &&
        (call.name === "scatter" || marker)
      ) {
        scatters.push({
          color,
          opacity,
          radius: getMarkerRadius(keyword, variables),
          x: [xValues],
          y: [yValues],
        });
        addBounds([xValues], [yValues]);
        return;
      }
      if (isNumberArray(xValues) && isNumberArray(yValues) && xValues.length === yValues.length) {
        const item = {
          color,
          dash,
          linewidth,
          opacity,
          radius: getMarkerRadius(keyword, variables),
          x: xValues,
          y: yValues,
        };
        if (call.name === "scatter") scatters.push(item);
        else plots.push(item);
        addBounds(xValues, yValues);
      } else {
        unsupportedCount += 1;
      }
      return;
    }

    if (call.name === "text") {
      const x = evaluateExpression(positional[0], variables);
      const y = evaluateExpression(positional[1], variables);
      const label = parsePreviewLabel(positional[2]);
      if (Number.isFinite(x) && Number.isFinite(y) && label !== null) {
        texts.push({
          fontFamily: parseString(keyword.fontfamily),
          fontSize: evaluateExpression(keyword.fontsize, variables),
          fontStyle: parseString(keyword.fontstyle),
          ha: parseString(keyword.ha || keyword.horizontalalignment),
          label,
          opacity,
          va: parseString(keyword.va || keyword.verticalalignment),
          x,
          y,
        });
        addBounds([x], [y]);
      } else {
        unsupportedCount += 1;
      }
      return;
    }

    if (call.name === "annotate") {
      const label = parsePreviewLabel(positional[0]);
      const xy = parseRangePair(keyword.xy || positional[1], variables);
      const xytext = parseRangePair(keyword.xytext, variables);
      const arrowStyle = parseStyleDict(keyword.arrowprops);
      if (xy && xytext && arrowStyle) {
        const arrowKeyword = arrowStyle.values;
        const patch = {
          type: "arrow",
          start: { x: xytext[0], y: xytext[1] },
          end: { x: xy[0], y: xy[1] },
          headSize: Math.max(6, Math.min(18, (evaluateExpression(arrowKeyword.mutation_scale, variables) || 20) * 0.45)),
          opacity,
          stroke: parseColor(arrowKeyword.color, "#202124"),
          strokeWidth: getLineWidth(arrowKeyword, variables, 2),
        };
        patches.push(patch);
        addPatchBounds(bounds, patch);
      }
      if (label !== null && xy) {
        if (label) texts.push({
          fontFamily: parseString(keyword.fontfamily),
          fontSize: evaluateExpression(keyword.fontsize, variables),
          fontStyle: parseString(keyword.fontstyle),
          ha: parseString(keyword.ha || keyword.horizontalalignment),
          label,
          opacity,
          va: parseString(keyword.va || keyword.verticalalignment),
          x: xy[0],
          y: xy[1],
        });
        if (label) addBounds([xy[0]], [xy[1]]);
      } else {
        unsupportedCount += 1;
      }
      return;
    }

    if (call.name === "axhline" || call.name === "axvline") {
      const value = evaluateExpression(keyword.y || keyword.x || positional[0] || "0", variables);
      if (Number.isFinite(value)) {
        if (call.name === "axhline") {
          plots.push({ color, dash, linewidth, opacity, x: [xlim?.[0] ?? -5, xlim?.[1] ?? 5], y: [value, value] });
          bounds.y.push(value);
        } else {
          plots.push({ color, dash, linewidth, opacity, x: [value, value], y: [ylim?.[0] ?? -5, ylim?.[1] ?? 5] });
          bounds.x.push(value);
        }
      } else {
        unsupportedCount += 1;
      }
      return;
    }

    if (call.name === "hlines") {
      const y = evaluateExpression(positional[0], variables);
      const x0 = evaluateExpression(positional[1], variables);
      const x1 = evaluateExpression(positional[2], variables);
      if ([y, x0, x1].every(Number.isFinite)) {
        plots.push({
          color: parseColor(keyword.colors || keyword.color, "#202124"),
          dash,
          linewidth,
          opacity,
          x: [x0, x1],
          y: [y, y],
        });
        addBounds([x0, x1], [y, y]);
      } else unsupportedCount += 1;
      return;
    }

    if (["xlim", "set_xlim", "ylim", "set_ylim"].includes(call.name)) {
      const values =
        positional.length === 1
          ? parseRangePair(positional[0], variables)
          : positional.map((item) => evaluateExpression(item, variables));
      if (values && Number.isFinite(values[0]) && Number.isFinite(values[1])) {
        if (call.name === "xlim" || call.name === "set_xlim") xlim = values;
        else ylim = values;
      }
      return;
    }

    if (["title", "set_title", "xlabel", "set_xlabel", "ylabel", "set_ylabel"].includes(call.name)) {
      const label = parseString(positional[0]);
      if (label && (call.name === "title" || call.name === "set_title")) title = label;
      if (label && (call.name === "xlabel" || call.name === "set_xlabel")) xlabel = label;
      if (label && (call.name === "ylabel" || call.name === "set_ylabel")) ylabel = label;
      return;
    }

    if (call.name === "set_aspect") equalAspect = parseString(positional[0]) === "equal";
    if (call.name === "axis") {
      const mode = parseString(positional[0]);
      if (mode === "equal") equalAspect = true;
      if (mode === "off") axisOff = true;
    }
  });

  const rawX = xlim || expandBounds(Math.min(...bounds.x), Math.max(...bounds.x));
  const rawY = ylim || expandBounds(Math.min(...bounds.y), Math.max(...bounds.y));
  return {
    axisOff,
    background,
    equalAspect,
    figureSize,
    hasMarks: Boolean(plots.length || scatters.length || texts.length || patches.length),
    patches,
    plots,
    scatters,
    texts,
    title,
    unsupportedCount,
    xlabel,
    xMax: rawX[1],
    xMin: rawX[0],
    yMax: rawY[1],
    yMin: rawY[0],
    ylabel,
  };
};

const getEqualAspectBounds = (parsed, width, height) => {
  let { xMin, xMax, yMin, yMax } = parsed;
  if (!parsed.equalAspect) return { xMin, xMax, yMin, yMax };

  const xSpan = Math.max(1e-9, xMax - xMin);
  const ySpan = Math.max(1e-9, yMax - yMin);
  const targetYSpan = xSpan * (height / width);
  const targetXSpan = ySpan * (width / height);
  if (ySpan < targetYSpan) {
    const mid = (yMin + yMax) / 2;
    yMin = mid - targetYSpan / 2;
    yMax = mid + targetYSpan / 2;
  } else if (xSpan < targetXSpan) {
    const mid = (xMin + xMax) / 2;
    xMin = mid - targetXSpan / 2;
    xMax = mid + targetXSpan / 2;
  }
  return { xMin, xMax, yMin, yMax };
};

const getTextAnchor = (value) => {
  if (value === "center") return "middle";
  if (value === "right") return "end";
  return "start";
};

const getDominantBaseline = (value) => {
  if (value === "center") return "middle";
  if (value === "top") return "text-before-edge";
  if (value === "bottom") return "text-after-edge";
  return "auto";
};

const getTextFontSize = (item, axisOff) => {
  if (Number.isFinite(item.fontSize)) {
    return Math.max(10, Math.min(axisOff ? 44 : 28, item.fontSize * 0.72));
  }
  return axisOff ? 30 : 15;
};

const getSvgLineCap = (value) => {
  if (value === "projecting") return "square";
  if (["butt", "round", "square"].includes(value)) return value;
  return "round";
};

const getSvgLineJoin = (value) => {
  if (["bevel", "miter", "round"].includes(value)) return value;
  return "round";
};

const getSketchFilter = (item) => (item?.handDrawn ? "url(#handdraw-jitter)" : undefined);

const buildSvgPathData = (patch, sx, sy) => {
  const commands = [];
  let index = 0;

  while (index < patch.vertices.length) {
    const point = patch.vertices[index];
    const code = patch.codes?.[index] || (index === 0 ? "MOVETO" : "LINETO");

    if (code === "STOP") break;
    if (code === "MOVETO") {
      commands.push(`M ${sx(point.x)} ${sy(point.y)}`);
      index += 1;
      continue;
    }
    if (code === "LINETO") {
      commands.push(`L ${sx(point.x)} ${sy(point.y)}`);
      index += 1;
      continue;
    }
    if (code === "CURVE3") {
      const end = patch.vertices[index + 1];
      if (end) {
        commands.push(`Q ${sx(point.x)} ${sy(point.y)} ${sx(end.x)} ${sy(end.y)}`);
        index += 2;
      } else {
        commands.push(`L ${sx(point.x)} ${sy(point.y)}`);
        index += 1;
      }
      continue;
    }
    if (code === "CURVE4") {
      const control2 = patch.vertices[index + 1];
      const end = patch.vertices[index + 2];
      if (control2 && end) {
        commands.push(
          `C ${sx(point.x)} ${sy(point.y)} ${sx(control2.x)} ${sy(control2.y)} ${sx(end.x)} ${sy(end.y)}`,
        );
        index += 3;
      } else {
        commands.push(`L ${sx(point.x)} ${sy(point.y)}`);
        index += 1;
      }
      continue;
    }
    if (code === "CLOSEPOLY") {
      commands.push("Z");
      index += 1;
      continue;
    }

    index += 1;
  }

  return commands.join(" ");
};

export default function MatplotlibQuickPreview({
  badgeAction = "Space + W 產生正式圖",
  badgeTitle = "快速預覽",
  code,
  compact = false,
  emptyTitle = "前端快速預覽尚無可畫內容",
  emptyMessage,
  image,
  loading,
  pending,
  unsupportedLabel = "產生正式圖",
  transparentBackground = false,
}) {
  if (loading) return null;
  if (!pending && image) {
    return <PreviewImage $transparent={transparentBackground} src={image} alt="matplotlib preview" $compact={compact} loading={compact ? "lazy" : undefined} decoding="async" />;
  }

  const parsed = parseQuestionMatplotlib(code);
  const figureRatio = parsed.figureSize
    ? Math.max(0.45, Math.min(2.4, parsed.figureSize.width / parsed.figureSize.height))
    : 720 / 460;
  const width = 720;
  const height = Math.round(width / figureRatio);
  const margin = parsed.axisOff
    ? { bottom: 14, left: 14, right: 14, top: 14 }
    : {
        bottom: parsed.xlabel ? 58 : 42,
        left: parsed.ylabel ? 68 : 52,
        right: 30,
        top: parsed.title ? 46 : 24,
      };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;
  const bounds = getEqualAspectBounds(parsed, chartWidth, chartHeight);
  const sx = (x) => margin.left + ((x - bounds.xMin) / (bounds.xMax - bounds.xMin)) * chartWidth;
  const sy = (y) => margin.top + chartHeight - ((y - bounds.yMin) / (bounds.yMax - bounds.yMin)) * chartHeight;
  const showQuick = pending && parsed.hasMarks;

  if (!showQuick && image) {
    return <PreviewImage $transparent={transparentBackground} src={image} alt="matplotlib preview" $compact={compact} loading={compact ? "lazy" : undefined} decoding="async" />;
  }

  if (!showQuick) {
    return (
      <QuickEmpty $compact={compact}>
        <strong>{emptyTitle}</strong>
        {!compact && (
          <span>
            {emptyMessage ||
              "支援 plot、scatter、text、Circle、Rectangle、Polygon、PathPatch 與 hand_line 手繪 helper。"}
          </span>
        )}
      </QuickEmpty>
    );
  }

  const xAxisY = bounds.yMin <= 0 && bounds.yMax >= 0 ? sy(0) : margin.top + chartHeight;
  const yAxisX = bounds.xMin <= 0 && bounds.xMax >= 0 ? sx(0) : margin.left;

  return (
    <QuickPreviewShell $compact={compact} $transparent={transparentBackground}>
      {!compact && (
        <QuickBadge data-record-export-exclude>
          <strong>{badgeTitle}</strong>
          <span>{badgeAction}</span>
        </QuickBadge>
      )}
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="question matplotlib svg preview">
        <defs>
          <filter id="handdraw-jitter" x="-4%" y="-4%" width="108%" height="108%">
            <feTurbulence baseFrequency="0.035" numOctaves="1" seed="7" result="noise" />
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="1.1" />
          </filter>
        </defs>
        <rect x="0" y="0" width={width} height={height} rx="8" fill={transparentBackground ? "transparent" : parsed.background} />
        {parsed.title && !parsed.axisOff && (
          <text x={width / 2} y="27" textAnchor="middle" className="title">
            {parsed.title}
          </text>
        )}
        {!parsed.axisOff && (
          <>
            <rect
              x={margin.left}
              y={margin.top}
              width={chartWidth}
              height={chartHeight}
              fill="#fbfbfb"
              stroke="#d5d8dc"
            />
            <line x1={margin.left} y1={xAxisY} x2={margin.left + chartWidth} y2={xAxisY} stroke="#42464d" />
            <line x1={yAxisX} y1={margin.top} x2={yAxisX} y2={margin.top + chartHeight} stroke="#42464d" />
          </>
        )}
        {parsed.patches.map((patch, index) => {
          if (patch.type === "arrow") {
            const x1 = sx(patch.start.x);
            const y1 = sy(patch.start.y);
            const x2 = sx(patch.end.x);
            const y2 = sy(patch.end.y);
            const length = Math.hypot(x2 - x1, y2 - y1);
            if (!length) return null;
            const ux = (x2 - x1) / length;
            const uy = (y2 - y1) / length;
            const head = Math.min(patch.headSize, length * 0.5);
            const wing = head * 0.38;
            const points = [
              [x2, y2],
              [x2 - ux * head - uy * wing, y2 - uy * head + ux * wing],
              [x2 - ux * head + uy * wing, y2 - uy * head - ux * wing],
            ].map(([x, y]) => `${x},${y}`).join(" ");
            return (
              <g key={`patch-${index}`} opacity={patch.opacity}>
                <line x1={x1} y1={y1} x2={x2 - ux * head * 0.65} y2={y2 - uy * head * 0.65}
                  stroke={patch.stroke} strokeWidth={patch.strokeWidth} />
                <polygon points={points} fill={patch.stroke} />
              </g>
            );
          }
          if (patch.type === "path") {
            return (
              <path
                key={`patch-${index}`}
                d={buildSvgPathData(patch, sx, sy)}
                fill={patch.fill}
                opacity={patch.opacity}
                stroke={patch.stroke}
                strokeWidth={patch.strokeWidth}
                strokeLinecap={getSvgLineCap(patch.capStyle)}
                strokeLinejoin={getSvgLineJoin(patch.joinStyle)}
                filter={getSketchFilter(patch)}
              />
            );
          }
          if (patch.type === "polygon") {
            const points = patch.vertices
              .map((point) => `${sx(point.x)},${sy(point.y)}`)
              .join(" ");
            const commonProps = {
              fill: patch.closed ? patch.fill : "none",
              stroke: patch.stroke,
              strokeWidth: patch.strokeWidth,
              strokeLinejoin: getSvgLineJoin(patch.joinStyle),
              opacity: patch.opacity,
            };
            return patch.closed ? (
              <polygon key={`patch-${index}`} points={points} {...commonProps} />
            ) : (
              <polyline key={`patch-${index}`} points={points} {...commonProps} />
            );
          }
          if (patch.type === "circle") {
            return (
              <circle
                key={`patch-${index}`}
                cx={sx(patch.center.x)}
                cy={sy(patch.center.y)}
                r={Math.abs(sx(patch.center.x + patch.radius) - sx(patch.center.x))}
                fill={patch.fill}
                opacity={patch.opacity}
                stroke={patch.stroke}
                strokeWidth={patch.strokeWidth}
              />
            );
          }
          return (
            <rect
              key={`patch-${index}`}
              x={sx(patch.origin.x)}
              y={sy(patch.origin.y + patch.height)}
              width={Math.abs(sx(patch.origin.x + patch.width) - sx(patch.origin.x))}
              height={Math.abs(sy(patch.origin.y) - sy(patch.origin.y + patch.height))}
              fill={patch.fill}
              opacity={patch.opacity}
              stroke={patch.stroke}
              strokeWidth={patch.strokeWidth}
            />
          );
        })}
        {parsed.plots.map((plot, index) => (
          <polyline
            key={`plot-${index}`}
            points={plot.x.map((x, pointIndex) => `${sx(x)},${sy(plot.y[pointIndex])}`).join(" ")}
            fill="none"
            stroke={plot.color}
            strokeWidth={plot.linewidth}
            strokeDasharray={plot.dash}
            opacity={plot.opacity}
            filter={getSketchFilter(plot)}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {parsed.scatters.flatMap((scatter, groupIndex) =>
          scatter.x.map((x, pointIndex) => (
            <circle
              key={`scatter-${groupIndex}-${pointIndex}`}
              cx={sx(x)}
              cy={sy(scatter.y[pointIndex])}
              r={scatter.radius}
              fill={scatter.color}
              opacity={scatter.opacity}
            />
          )),
        )}
        {parsed.texts.map((item, index) => (
          <text
            key={`text-${index}`}
            x={sx(item.x)}
            y={sy(item.y)}
            textAnchor={getTextAnchor(item.ha)}
            dominantBaseline={getDominantBaseline(item.va)}
            className="label"
            opacity={item.opacity}
            style={{
              fontFamily: item.fontFamily || undefined,
              fontSize: `${getTextFontSize(item, parsed.axisOff)}px`,
              fontStyle: item.fontStyle || undefined,
            }}
          >
            {item.label}
          </text>
        ))}
        {!parsed.axisOff && (
          <>
            <text x={margin.left} y={height - 13} className="axis-label">{bounds.xMin.toFixed(2)}</text>
            <text x={margin.left + chartWidth} y={height - 13} textAnchor="end" className="axis-label">{bounds.xMax.toFixed(2)}</text>
            <text x="10" y={margin.top + 12} className="axis-label">{bounds.yMax.toFixed(2)}</text>
            <text x="10" y={margin.top + chartHeight} className="axis-label">{bounds.yMin.toFixed(2)}</text>
            {parsed.xlabel && (
              <text x={margin.left + chartWidth / 2} y={height - 17} textAnchor="middle" className="axis-label">
                {parsed.xlabel}
              </text>
            )}
            {parsed.ylabel && (
              <text transform={`translate(19 ${margin.top + chartHeight / 2}) rotate(-90)`} textAnchor="middle" className="axis-label">
                {parsed.ylabel}
              </text>
            )}
          </>
        )}
      </svg>
      {!compact && parsed.unsupportedCount > 0 && (
        <QuickNote>部分進階語法會以後端正式圖片為準；按 Space + W {unsupportedLabel}。</QuickNote>
      )}
    </QuickPreviewShell>
  );
}

const PreviewImage = styled.img`
  width: 100%;
  max-height: ${({ $compact }) => ($compact ? "74px" : "min(620px, 68vh)")};
  height: ${({ $compact }) => ($compact ? "74px" : "auto")};
  object-fit: contain;
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  background: ${({ $transparent }) => $transparent ? "transparent" : "#fff"};
`;

const QuickPreviewShell = styled.div`
  position: relative;
  min-height: ${({ $compact }) => ($compact ? "74px" : "220px")};
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  background: ${({ $transparent }) => $transparent ? "transparent" : "#fff"};
  overflow: hidden;

  svg {
    display: block;
    width: 100%;
    height: ${({ $compact }) => ($compact ? "74px" : "auto")};
  }

  .label {
    fill: #202124;
    font-weight: 800;
  }

  .title {
    fill: #202124;
    font-size: 17px;
    font-weight: 850;
  }

  .axis-label {
    fill: #5f6368;
    font-size: 12px;
    font-weight: 700;
  }
`;

const QuickBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(26, 115, 232, 0.24);
  border-radius: 999px;
  padding: 5px 9px;
  background: rgba(255, 255, 255, 0.88);
  color: #174ea6;
  font-size: 12px;
  backdrop-filter: blur(2px);

  strong {
    font-weight: 900;
  }
`;

const QuickNote = styled.div`
  border-top: 1px solid var(--app-border, #eceff1);
  padding: 8px 10px;
  color: var(--app-subtle-text, #4a4f55);
  font-size: 12px;
  font-weight: 750;
`;

const QuickEmpty = styled.div`
  min-height: ${({ $compact }) => ($compact ? "74px" : "220px")};
  display: grid;
  place-items: center;
  align-content: center;
  gap: 6px;
  border: 1px dashed var(--app-border, #cfcfcf);
  border-radius: 8px;
  background: var(--app-surface-soft, #f7f7f7);
  color: var(--app-muted-text, #6b6b6b);
  padding: 10px;
  text-align: center;

  strong {
    color: var(--app-subtle-text, #3d3d3d);
    font-size: ${({ $compact }) => ($compact ? "10px" : "14px")};
  }

  span {
    font-size: 12px;
    font-weight: 700;
  }
`;
